import type { AxiosResponse } from 'axios';
import { bunja } from 'bunja';
import { useBunja } from 'bunja/react';
import { atom, useAtom, useAtomValue } from 'jotai';
import { atomWithMutation, queryClientAtom } from 'jotai-tanstack-query';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';

import type {
  PostDefiYieldRedeemConfirmRequest,
  PostDefiYieldRedeemConfirmResponse,
  PostDefiYieldRedeemPreviewRequest,
  PostDefiYieldRedeemPreviewResponse,
} from '@flipster/apis/v1/dynamic-earn/types';
import { type DynamicEarnRedeemPeriodType } from '@flipster/apis/v1/dynamic-earn/types';
import { FormattedMessage, i18nBunja, type MessageKey } from '@flipster/i18n';
import { overlayBunja } from '@flipster/overlay';
import { Slider } from '@flipster/react/design-system-legacy/slider';
import { Badge } from '@flipster/react/design-system/badge';
import { IconButton } from '@flipster/react/design-system/icon-button';
import { Modal } from '@flipster/react/design-system/modal';
import { RadioButton } from '@flipster/react/design-system/radio-button';
import { Tooltip } from '@flipster/react/design-system/tooltip';
import Formatted from '@flipster/react/design-utils/formatted';
import { FormattedNumberInput } from '@flipster/react/design-utils/formatted-input';
import ChevronDownIcon from '@flipster/react/icon/generated/ChevronDownIcon';
import { css } from '@flipster/styled-system/css';
import { center, hstack, stack } from '@flipster/styled-system/patterns';
import { JotaiStoreScope } from '@flipster/utils/bunja-jotai';
import { UsdtCurrency, UsdtPrecision } from '@flipster/utils/constants/currency';
import { getErrorMessage } from '@flipster/utils/error';
import Num, { formatToShortNumber } from '@flipster/utils/num';
import segment from '@flipster/utils/segment';
import { DynamicEarnEvent } from '@flipster/utils/segment/events/dynamic-earn';

import { CurrencyDisplay } from '../../../misc/display';
import { AgreeCheckbox, type TermsLink } from '../../main-page/misc/agree-checkbox';
import { RedeemResultModal, type RedeemResult } from './redeem-result-modal';

interface RedeemModalConfig {
  redeemOverviewBunja: import('../state').ProductRedeemOverviewBunja;
  previewApi: (params: PostDefiYieldRedeemPreviewRequest) => Promise<AxiosResponse<PostDefiYieldRedeemPreviewResponse>>;
  confirmApi: (params: PostDefiYieldRedeemConfirmRequest) => Promise<AxiosResponse<PostDefiYieldRedeemConfirmResponse>>;
  overviewQueryKey: string;
  productName: string;
  terms: TermsLink[];
  fromLabelId: MessageKey;
}

export function createRedeemModal(config: RedeemModalConfig) {
  const {
    redeemOverviewBunja: overviewBunja,
    previewApi,
    confirmApi,
    overviewQueryKey,
    productName,
    terms,
    fromLabelId,
  } = config;

  // ─── Bunja ───────────────────────────────────────────────

  const modalBunja = bunja(() => {
    const store = bunja.use(JotaiStoreScope);
    const overlay = bunja.use(overlayBunja);
    const { getT } = bunja.use(i18nBunja);
    const t = getT();
    const { availableBalanceAtom, minAmountAtom, instantLeftCapAtom } = bunja.use(overviewBunja);

    const periodTypeAtom = atom<DynamicEarnRedeemPeriodType | undefined>(undefined);
    const agreedAtom = atom(false);
    const amountAtom = atom<string>('');
    const isDirtyAtom = atom((get) => get(amountAtom) !== '');

    const percentageAtom = atom(
      (get) => {
        const availableBalance = get(availableBalanceAtom);
        const amount = get(amountAtom);
        if (availableBalance.isZero()) return 0;

        const amountNum = Num(amount).isFinite() ? Num(amount) : Num(0);
        const percentage = amountNum.div(availableBalance).times(100).toNumber();
        return Math.min(percentage, 100);
      },
      (get, set, newPercentage: number) => {
        const availableBalance = get(availableBalanceAtom);
        const calculatedAmount = availableBalance.div(100).times(newPercentage).toFixed(2);
        set(amountAtom, calculatedAmount);
      },
    );

    const errorMessageAtom = atom((get) => {
      const minAmount = get(minAmountAtom);
      const amount = get(amountAtom);
      const isDirty = get(isDirtyAtom);
      const instantLeftCap = get(instantLeftCapAtom);
      const periodType = get(periodTypeAtom);

      if (!isDirty) return null;
      if (Num(amount).lt(minAmount)) {
        return t('Redeem.r251008.You_need_to_redeem_at_least_amount_quote_currency', {
          amountQuoteCurrency: `${minAmount} USDT`,
        });
      }
      if (periodType === 'INSTANT' && Num(amount).gt(instantLeftCap)) {
        return t('Earn.r260108.Up_to_amount_quote_currency_available_for_instant_redemption', {
          amountQuoteCurrency: `${formatToShortNumber(instantLeftCap.toString())} USDT`,
        });
      }
      return null;
    });

    const isValidAtom = atom((get) => {
      const isDirty = get(isDirtyAtom);
      const agreed = get(agreedAtom);
      const errorMessage = get(errorMessageAtom);
      return isDirty && agreed && !errorMessage;
    });

    const redeemPreviewMutaionAtom = atomWithMutation((get) => {
      const amount = get(amountAtom);
      const type = get(periodTypeAtom) ?? 'STANDARD';
      return {
        mutationFn: () => previewApi({ fromAmountInUsdt: amount, type }),
      };
    });

    const previewIdAtom = atom<number | undefined>(undefined);
    const redeemConfirmMutaionAtom = atomWithMutation((get) => {
      const queryClient = get(queryClientAtom);
      const previewId = get(previewIdAtom);
      return {
        mutationFn: () => confirmApi({ previewId: previewId! }),
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: [overviewQueryKey] });
        },
      };
    });

    async function redeem() {
      let result: RedeemResult;
      try {
        const previewResult = await store.get(redeemPreviewMutaionAtom).mutateAsync();
        if (previewResult.data?.id) {
          store.set(previewIdAtom, previewResult.data.id);
          const confirmResult = await store.get(redeemConfirmMutaionAtom).mutateAsync();
          result = {
            type: 'success',
            previewResult: previewResult.data!,
            confirmResult: confirmResult.data!,
          };
        } else {
          return;
        }
      } catch (error) {
        result = { type: 'error', message: getErrorMessage(error) };
      }
      overlay.open(({ isOpen, onClose, unmount }) => (
        <RedeemResultModal
          isOpen={isOpen}
          onClose={onClose}
          onExit={unmount}
          result={result}
          fromLabelId={fromLabelId}
        />
      ));
    }

    const isSubmittingAtom = atom((get) => {
      return get(redeemPreviewMutaionAtom).isPending || get(redeemConfirmMutaionAtom).isPending;
    });

    return {
      periodTypeAtom,
      agreedAtom,
      amountAtom,
      percentageAtom,
      errorMessageAtom,
      isValidAtom,
      redeem,
      isSubmittingAtom,
    };
  });
  modalBunja.debugLabel = `redeemModalBunja(${productName})`;

  // ─── UI Components ───────────────────────────────────────

  const SelectType = () => {
    const [expanded, setExpanded] = useState(true);
    const {
      availableBalanceAtom,
      instantRedeemInfoAtom,
      standardEstimatedProcessingHoursAtom,
      standardRedeemInfoAtom,
      instantLeftCapAtom,
      isInstantAvailableAtom,
    } = useBunja(overviewBunja);
    const { periodTypeAtom } = useBunja(modalBunja);

    const availableBalance = useAtomValue(availableBalanceAtom);
    const [periodType, setPeriodType] = useAtom(periodTypeAtom);
    const instantRedeemInfo = useAtomValue(instantRedeemInfoAtom);
    const instantLeftCap = useAtomValue(instantLeftCapAtom);
    const standardRedeemInfo = useAtomValue(standardRedeemInfoAtom);
    const standardEstimatedProcessingHours = useAtomValue(standardEstimatedProcessingHoursAtom);
    const isInstantAvailable = useAtomValue(isInstantAvailableAtom);

    return (
      <>
        <div className={stack({ gap: 12, py: 12, px: 16, rounded: 8, bg: 'background.default.level-1' })}>
          <div className={hstack({ justify: 'space-between' })}>
            <p className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-2' })}>
              <FormattedMessage id='Autogen_common.r251126.s_Type' />
            </p>
            <div className={hstack({ gap: 8, color: 'content.default.level-5' })}>
              {!expanded && (
                <>
                  {periodType === 'INSTANT' && (
                    <>
                      <p className={css({ textStyle: 'Text/L/Strong' })}>
                        <FormattedMessage id='Earn.r260108.Instant' />
                      </p>
                      <Badge type='tint' color='positive'>
                        <FormattedMessage
                          id='Earn.r260108.Amount_cap_left'
                          values={{ amount: <Formatted.ShortNumber value={instantLeftCap} precision={2} /> }}
                        />
                      </Badge>
                    </>
                  )}
                  {periodType === 'STANDARD' && (
                    <p className={css({ textStyle: 'Text/L/Strong' })}>
                      <FormattedMessage id='Earn.r260108.Standard' />
                    </p>
                  )}
                </>
              )}
              <IconButton
                icon={<ChevronDownIcon className={css({ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' })} />}
                hierarchy='quaternary'
                size='small'
                onClick={() => setExpanded(!expanded)}
                disabled={!periodType}
              />
            </div>
          </div>
          {expanded && (
            <div className={stack({ gap: 4, pb: 4 })}>
              <div
                className={stack({
                  justify: 'center',
                  gap: 8,
                  p: 16,
                  borderColor: 'border.default.level-1',
                  borderWidth: '1px',
                  borderStyle: isInstantAvailable ? 'solid' : 'dashed',
                  rounded: 8,
                })}
              >
                <div className={hstack({ gap: 8 })}>
                  <RadioButton
                    checked={periodType === 'INSTANT'}
                    onChange={() => {
                      segment.track(DynamicEarnEvent.DynamicEarnRedemptionTypeClick, {
                        product_name: productName,
                        redemption_option: 'Instant',
                      });
                      setPeriodType('INSTANT');
                      setExpanded(false);
                    }}
                    disabled={!isInstantAvailable}
                  >
                    <FormattedMessage id='Earn.r260108.Instant' />
                  </RadioButton>
                  {isInstantAvailable ? (
                    <Badge type='tint' color='positive'>
                      <FormattedMessage
                        id='Earn.r260108.Amount_cap_left'
                        values={{ amount: <Formatted.ShortNumber value={instantLeftCap} precision={2} /> }}
                      />
                    </Badge>
                  ) : (
                    <Badge type='tint' color='neutral'>
                      <FormattedMessage id='Common.r260108.Unavailable' />
                    </Badge>
                  )}
                </div>
                <div className={stack({ gap: 2, pl: 24 })}>
                  <div className={hstack({ justify: 'space-between', gap: 8 })}>
                    <p className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-2' })}>
                      <FormattedMessage id='Autogen_common.r251126.s_Est_processing_time' />
                    </p>
                    <p
                      className={css({
                        textStyle: 'Text/M/Monospace/Regular',
                        color: isInstantAvailable ? 'content.default.level-5' : 'content.default.level-1',
                        textAlign: 'right',
                      })}
                    >
                      <FormattedMessage id='Earn.r260108.Instant' />
                    </p>
                  </div>
                  <div className={hstack({ justify: 'space-between', gap: 8 })}>
                    <p className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-2' })}>
                      <FormattedMessage id='Common.r251204.Fee' />
                    </p>
                    <Formatted.Percent
                      value={instantRedeemInfo?.feeRate}
                      isRate
                      css={{
                        textStyle: 'Text/M/Monospace/Regular',
                        color: isInstantAvailable ? 'content.default.level-5' : 'content.default.level-1',
                      }}
                    />
                  </div>
                </div>
              </div>
              <div
                className={stack({
                  justify: 'center',
                  gap: 8,
                  p: 12,
                  borderColor: 'border.default.level-1',
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  rounded: 8,
                })}
              >
                <div className={hstack({ gap: 8 })}>
                  <RadioButton
                    checked={periodType === 'STANDARD'}
                    onChange={() => {
                      segment.track(DynamicEarnEvent.DynamicEarnRedemptionTypeClick, {
                        product_name: productName,
                        redemption_option: 'Standard',
                      });
                      setPeriodType('STANDARD');
                      setExpanded(false);
                    }}
                  >
                    <FormattedMessage id='Earn.r260108.Standard' />
                  </RadioButton>
                </div>
                <div className={stack({ gap: 2, pl: 24 })}>
                  <div className={hstack({ justify: 'space-between', gap: 8 })}>
                    <Tooltip.Root placement='right' maxWidth={280}>
                      <Tooltip.TextTrigger
                        label={<FormattedMessage id='Autogen_common.r251126.s_Est_processing_time' />}
                        css={{ textStyle: 'Text/S/Regular' }}
                      />
                      <Tooltip.Content
                        bottomSheetTitle={<FormattedMessage id='Autogen_common.r251126.s_Est_processing_time' />}
                      >
                        <FormattedMessage
                          id='Earn.r251211.Est_processing_time_desc_with_hours'
                          values={{ hours: '48' }}
                        />
                      </Tooltip.Content>
                    </Tooltip.Root>
                    <p
                      className={css({
                        textStyle: 'Text/M/Monospace/Regular',
                        color: 'content.default.level-5',
                        textAlign: 'right',
                      })}
                    >
                      <FormattedMessage
                        id='Earn.r251204.Within_hours_duration'
                        values={{ hour: standardEstimatedProcessingHours }}
                      />
                    </p>
                  </div>
                  <div className={hstack({ justify: 'space-between', gap: 8 })}>
                    <p className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-2' })}>
                      <FormattedMessage id='Common.r251204.Fee' />
                    </p>
                    <Formatted.Percent
                      value={standardRedeemInfo?.feeRate}
                      isRate
                      css={{
                        textStyle: 'Text/M/Monospace/Regular',
                        color: 'content.default.level-5',
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
        {periodType === undefined && (
          <div
            className={hstack({
              justify: 'space-between',
              py: 12,
              px: 16,
              rounded: 8,
              bg: 'background.default.level-1',
            })}
          >
            <div className={hstack()}>
              <p className={css({ textStyle: 'Text/S/Regular', color: 'content.default.level-2' })}>
                <FormattedMessage id='Earn.r251204.You_redeem' />
              </p>
            </div>
            <p
              className={css({
                textStyle: 'Text/S/Monospace/Regular',
                color: 'content.default.level-5',
                textAlign: 'right',
              })}
            >
              <FormattedMessage id='Common.r251107.Available' />{' '}
              <Formatted.Number value={availableBalance} suffix={UsdtCurrency} precision={UsdtPrecision} toFixed />
            </p>
          </div>
        )}
      </>
    );
  };

  const EnterAmount = () => {
    const t = useTranslations();
    const {
      redeemOverviewAtom,
      availableBalanceAtom,
      minAmountAtom,
      standardRedeemInfoAtom,
      standardEstimatedProcessingHoursAtom,
      instantLeftCapAtom,
      instantRedeemInfoAtom,
    } = useBunja(overviewBunja);
    const { amountAtom, percentageAtom, errorMessageAtom, periodTypeAtom } = useBunja(modalBunja);
    const redeemOverview = useAtomValue(redeemOverviewAtom);
    const availableBalance = useAtomValue(availableBalanceAtom);
    const [amount, setAmount] = useAtom(amountAtom);
    const [percentage, setPercentage] = useAtom(percentageAtom);
    const minAmount = useAtomValue(minAmountAtom);
    const errorMessage = useAtomValue(errorMessageAtom);
    const standardRedeemInfo = useAtomValue(standardRedeemInfoAtom);
    const instantLeftCap = useAtomValue(instantLeftCapAtom);
    const standardEstimatedProcessingHours = useAtomValue(standardEstimatedProcessingHoursAtom);
    const periodType = useAtomValue(periodTypeAtom);
    const [hasBeenDirty, setHasBeenDirty] = useState(false);
    const isInstant = periodType === 'INSTANT';
    const isStandard = periodType === 'STANDARD';
    const instantRedeemInfo = useAtomValue(instantRedeemInfoAtom);

    useEffect(() => {
      if (!hasBeenDirty && amount !== '') {
        setHasBeenDirty(true);
      }
    }, [amount, hasBeenDirty]);

    return (
      <>
        <div
          className={stack({
            gap: 20,
            px: 16,
            pt: 16,
            pb: 20,
            rounded: 8,
            bg: 'background.default.level-1',
          })}
        >
          <div className={stack({ gap: 8 })}>
            <div className={hstack({ justify: 'space-between' })}>
              <span
                className={css({
                  textStyle: 'Text/S/Regular',
                  color: 'content.default.level-2',
                })}
              >
                <FormattedMessage id='Earn.r251204.You_redeem' />
              </span>
              <span
                className={css({
                  textStyle: 'Text/S/Monospace/Regular',
                  color: 'content.default.level-3',
                  textAlign: 'right',
                })}
              >
                <FormattedMessage id='Common.r251107.Available' />{' '}
                <Formatted.Number value={availableBalance} suffix={UsdtCurrency} precision={UsdtPrecision} toFixed />
              </span>
            </div>
            <div className={hstack({ gap: 8 })}>
              <FormattedNumberInput
                value={amount}
                onChange={setAmount}
                placeholder={t('Common.r251204.Min_amount', { amount: minAmount })}
                inputMode='decimal'
                precision={UsdtPrecision}
                max={availableBalance.toString()}
                className={css({ flex: 1, textStyle: 'Title/M/Monospace', color: 'content.default.level-5' })}
              />
              <div
                className={center({
                  w: 88,
                  h: 36,
                  rounded: 1000,
                  bg: 'background.accent.secondary.tint',
                })}
              >
                <CurrencyDisplay
                  currency={UsdtCurrency}
                  icon={<CurrencyDisplay.Icon size={20} space={4} />}
                  mainLabel={<CurrencyDisplay.Label value='shortName' />}
                />
              </div>
            </div>

            <div className={hstack({ h: 17, textStyle: 'Text/S/Regular' })}>
              {periodType === 'INSTANT' && !hasBeenDirty && (
                <p className={css({ color: 'content.default.level-3' })}>
                  <FormattedMessage
                    id='Earn.r260108.Up_to_amount_quote_currency_available_for_instant_redemption'
                    values={{
                      amountQuoteCurrency: (
                        <Formatted.ShortNumber value={instantLeftCap} suffix={UsdtCurrency} precision={2} />
                      ),
                    }}
                  />
                </p>
              )}
              {errorMessage && <p className={css({ color: 'content.state.negative' })}>{errorMessage}</p>}
            </div>
          </div>
          <Slider
            value={percentage}
            onChange={setPercentage}
            marks={[0, 25, 50, 75, 100]}
            getLabel={(mark) => `${mark}%`}
            css={{ '& button': { textStyle: 'Text/S/Regular', color: 'content.default.level-2!' } }}
          />
        </div>
        <div
          className={stack({
            gap: 8,
            py: 12,
            px: 16,
            borderColor: 'border.default.level-1',
            borderWidth: '1px',
            borderStyle: 'solid',
            rounded: 8,
          })}
        >
          <div className={hstack({ justify: 'space-between' })}>
            <Tooltip.Root placement='right' maxWidth={280}>
              <Tooltip.TextTrigger
                label={<FormattedMessage id='Earn.r260304.You_receive' />}
                css={{ textStyle: 'Text/S/Monospace/Regular' }}
              />
              <Tooltip.Content bottomSheetTitle={<FormattedMessage id='Earn.r260304.You_receive' />}>
                <FormattedMessage id='Earn.r251204.Redemption_amount_variance_notice_with_fees' />
              </Tooltip.Content>
            </Tooltip.Root>
            <span
              className={css({
                textStyle: 'Text/M/Monospace/Regular',
                color: 'content.default.level-5',
                textAlign: 'right',
              })}
            >
              {isInstant &&
                (amount === '' ? (
                  '-'
                ) : (
                  <Formatted.Number
                    value={Num(amount).multipliedBy(Num(1).minus(Num(instantRedeemInfo?.feeRate ?? '0')))}
                    suffix={UsdtCurrency}
                    precision={UsdtPrecision}
                    toFixed
                  />
                ))}
              {isStandard && <FormattedMessage id='Earn.r251204.Determined_at_execution_value' />}
            </span>
          </div>

          <div className={hstack({ justify: 'space-between' })}>
            <Tooltip.Root placement='right' maxWidth={280}>
              <Tooltip.TextTrigger
                label={<FormattedMessage id='Earn.r260304.Est_shares_to_redeem' />}
                css={{ textStyle: 'Text/S/Monospace/Regular' }}
              />
              <Tooltip.Content bottomSheetTitle={<FormattedMessage id='Earn.r260304.Est_shares_to_redeem' />}>
                <FormattedMessage id='Earn.r260304.Est_shares_to_redeem_tooltip' />
              </Tooltip.Content>
            </Tooltip.Root>
            <span className={css({ textStyle: 'Text/M/Monospace/Regular', color: 'content.default.level-5' })}>
              {amount === '' ? (
                '-'
              ) : (
                <Formatted.Number
                  value={Num(amount)
                    .dividedBy(Num(redeemOverview?.shareTokenPrice ?? '1'))
                    .toFixed(6)}
                  precision={6}
                  toFixed
                />
              )}
            </span>
          </div>

          <div className={hstack({ justify: 'space-between' })}>
            <span className={css({ textStyle: 'Text/S/Monospace/Regular', color: 'content.default.level-2' })}>
              <FormattedMessage id='Earn.r251208.Share_token_price' />
            </span>
            <span className={css({ textStyle: 'Text/M/Monospace/Regular', color: 'content.default.level-5' })}>
              <Formatted.Number value={redeemOverview?.shareTokenPrice} precision={6} toFixed />
            </span>
          </div>

          <div className={hstack({ justify: 'space-between' })}>
            <span className={css({ textStyle: 'Text/S/Monospace/Regular', color: 'content.default.level-2' })}>
              <FormattedMessage id='Autogen_common.r251126.s_Est_processing_time' />
            </span>
            <span
              className={css({
                textStyle: 'Text/M/Monospace/Regular',
                color: 'content.default.level-5',
                textAlign: 'right',
              })}
            >
              {isInstant && <FormattedMessage id='Earn.r260108.Instant' />}
              {isStandard && (
                <FormattedMessage
                  id='Earn.r251204.Within_hours_duration'
                  values={{ hour: standardEstimatedProcessingHours }}
                />
              )}
            </span>
          </div>

          <div className={hstack({ justify: 'space-between' })}>
            <span
              className={css({
                textStyle: 'Text/S/Monospace/Regular',
                color: 'content.default.level-2',
              })}
            >
              <FormattedMessage id='Common.r251204.Fee' />
            </span>
            <span
              className={css({
                textStyle: 'Text/M/Monospace/Regular',
                color: 'content.default.level-5',
                textAlign: 'right',
              })}
            >
              {isInstant && <Formatted.Percent value={instantRedeemInfo?.feeRate} isRate precision={2} toFixed />}
              {isStandard && <Formatted.Percent value={standardRedeemInfo?.feeRate} isRate precision={2} toFixed />}
            </span>
          </div>
        </div>
      </>
    );
  };

  // ─── Modal ───────────────────────────────────────────────

  const RedeemModalContent = ({ onClose }: { onClose: () => void }) => {
    const { availableBalanceAtom } = useBunja(overviewBunja);
    const { periodTypeAtom, redeem, agreedAtom, isValidAtom, isSubmittingAtom, amountAtom } = useBunja(modalBunja);
    const periodType = useAtomValue(periodTypeAtom);
    const [agreed, setAgreed] = useAtom(agreedAtom);
    const isValid = useAtomValue(isValidAtom);
    const isSubmitting = useAtomValue(isSubmittingAtom);
    const amount = useAtomValue(amountAtom);
    const availableBalance = useAtomValue(availableBalanceAtom);

    return (
      <>
        <Modal.Header>
          <Modal.Title label={<FormattedMessage id='Earn.r251204.Enter_redemption_amount' />} />
        </Modal.Header>
        <Modal.Content>
          <div className={stack({ gap: 4 })}>
            <SelectType />
            {periodType && <EnterAmount />}
          </div>
          <div className={css({ mt: 24 })}>
            <AgreeCheckbox agreed={agreed} onChange={setAgreed} terms={terms} />
          </div>
        </Modal.Content>
        <Modal.Footer>
          <Modal.Button
            label={<FormattedMessage id='Common.r251008.Redeem' />}
            size='large'
            disabled={!isValid}
            isLoading={isSubmitting}
            onClick={async () => {
              segment.track(DynamicEarnEvent.DynamicEarnRedemptionConfirmClick, {
                product_name: productName,
                redemption_amount: amount,
                available_amount: availableBalance.toString(),
              });
              await redeem();
              onClose();
            }}
          />
        </Modal.Footer>
      </>
    );
  };

  return { RedeemModalContent, redeemModalBunja: modalBunja };
}
